// The My Desk page header's one small menu — Request Ledger N-090b (owner,
// 2026-09-08), reworked 2026-10-12 (owner): "Let's just have a check box
// next to each desk item for show / don't show, except the following which
// need to be on permanently: Needs attention, Communications, SPC,
// nvRecruiter, My Mail."
//
// So the menu is a checklist of every panel type this role may see:
//   - checked = on the Desk. Ticking adds it at the first free spot;
//     unticking removes every copy of that type.
//   - a permanent type (catalog `permanent`) is checked and disabled,
//     "Always on" — src/desk.js adds it back to any layout missing it.
//   - a type can never be added twice (the 2026-10-12 duplicate bug): a
//     ticked box has nothing left to add, and a clinic panel's first copy
//     takes the first clinic not already on the Desk (nextAddableConfig).
// The panel's description lives here, in the list — not on the panel.
// "Reset layout" stays at the bottom of the same menu.
//
// Pure UI plumbing: where a new panel lands (firstFreeSpot) and what is on
// the canvas (visiblePanelsFor) are shared with DeskGrid.tsx via
// client/src/desk/{gridPack,panelCatalog}.ts.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { Role } from '../lib/nav';
import { visibleClinics } from './ClinicTabs';
import {
  catalogEntry, catalogForRole, visiblePanelsFor, nextAddableConfig,
  type DeskLayout, type DeskPanelInstance, type PanelType,
} from '../desk/panelCatalog';
import { defaultLayoutFor } from '../desk/defaultLayouts';
import { firstFreeSpot, newPanelId } from '../desk/gridPack';

export interface DeskAddMenuProps {
  layout: DeskLayout;
  role: Role;
  onChange: (layout: DeskLayout) => void;
  /** What Reset layout goes back to — for a manager or director, Tomer's
   * own Desk as they see it (owner, 2026-10-12: his layout carries to
   * everyone). Default: the role's shipped default. */
  resetTo?: DeskLayout;
}

export function DeskAddMenu({
  layout, role, onChange, resetTo,
}: DeskAddMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const clinics = visibleClinics();
  const panels = visiblePanelsFor(layout, role);

  function commit(nextPanels: DeskPanelInstance[]): void {
    onChange({ version: 1, panels: nextPanels });
  }

  function addPanel(type: PanelType): void {
    const entry = catalogEntry(type);
    if (!entry) return;
    // Never a second copy of a panel already on the Desk (owner,
    // 2026-10-12) — null means every allowed copy is already there.
    const config = nextAddableConfig(entry, panels, clinics);
    if (!config) return;
    const { w, h } = entry.defaultSize;
    const { col, row } = firstFreeSpot(panels, w, h);
    const panel: DeskPanelInstance = {
      id: newPanelId(), type, w, h, col, row,
    };
    if (entry.clinicConfigurable) panel.config = config;
    commit([...panels, panel]);
  }

  function removeType(type: PanelType): void {
    if (catalogEntry(type)?.permanent) return;
    commit(panels.filter((p) => p.type !== type));
  }

  function toggle(type: PanelType, onDesk: boolean): void {
    if (onDesk) removeType(type);
    else addPanel(type);
  }

  function resetToDefault(): void {
    commit((resetTo ?? defaultLayoutFor(role)).panels);
    setOpen(false);
  }

  return (
    <div class="desk-header-actions" data-testid="desk-toolbar">
      <button
        type="button"
        class="btn"
        data-testid="desk-add-panel"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Show / hide panels
      </button>
      {open ? (
        <div class="desk-add-panel-menu" data-testid="desk-add-panel-menu">
          {/* Owner, 2026-10-12: the always-on panels are not listed — they are
              always on, so there is nothing to choose. */}
          {catalogForRole(role).filter((entry) => !entry.permanent).map((entry) => {
            const onDesk = panels.some((p) => p.type === entry.type);
            const permanent = Boolean(entry.permanent);
            return (
              <label
                key={entry.type}
                class={`desk-add-panel-item desk-panel-check${permanent ? ' desk-panel-check-fixed' : ''}`}
                data-testid={`desk-add-panel-row-${entry.type}`}
              >
                <input
                  type="checkbox"
                  data-testid={`desk-add-panel-item-${entry.type}`}
                  checked={permanent || onDesk}
                  disabled={permanent}
                  onChange={() => toggle(entry.type, onDesk)}
                />
                <span class="desk-panel-check-text">
                  <b>
                    {entry.label}
                    {permanent ? <em class="desk-panel-check-always"> · Always on</em> : null}
                  </b>
                  {entry.description ? <span>{entry.description}</span> : null}
                </span>
              </label>
            );
          })}
          <div class="desk-add-panel-menu-divider" role="separator" />
          <button
            type="button"
            class="desk-add-panel-item"
            data-testid="desk-reset-default"
            onClick={resetToDefault}
          >
            <b>Reset layout</b>
            <span>Back to the shipped default for your role.</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
